import React, { useEffect, useState } from 'react';
import { ApprovalPolicyStep, ApprovalStatus, ApproverOption, approvalsApi } from './api';

/** The selected reviewer is an approval identity, never a login identity.
 * The server checks eligibility/turn and records the signed-in Root actor. */
export function useApprovalActor({ root, status, step, targetId, enabled = true, options }: {
  root: boolean; status?: ApprovalStatus | null; step: ApprovalPolicyStep;
  targetId: string; enabled?: boolean; options?: ApproverOption[];
}) {
  const [selected, select] = useState('');
  const [eligible, setEligible] = useState<ApproverOption[]>([]);
  useEffect(() => { select(''); }, [targetId]);
  useEffect(() => {
    if (!root || !enabled || options || !status || status.approvers.length) return;
    let cancelled = false;
    approvalsApi.getPolicies().then(result => {
      if (!cancelled) setEligible(result.eligible?.[step] || []);
    }).catch(() => { if (!cancelled) setEligible([]); });
    return () => { cancelled = true; };
  }, [root, enabled, step, status, options]);
  const choices = options || (status?.approvers.length ? status.approvers : eligible);
  const available = choices.filter(person => !status?.approvals.some(vote => vote.user_id === person.id)
    && (!status?.ordered || status.turn?.id === person.id));
  const selectedAvailable = !!selected && available.some(person => person.id === selected);
  const canApprove = root && selected
    ? selectedAvailable
    : status ? status.me.can_approve && !status.me.has_approved : true;
  return {
    selected, select, choices, available,
    canApprove: (status?.stage_review || status?.architecture_review)?.phase === 'unavailable' ? false : canApprove,
    body: root && selectedAvailable ? { approve_as_user_id: selected } : {},
  };
}

export default function ApproveAsSelector({ actor, disabled = false }: {
  actor: ReturnType<typeof useApprovalActor>; disabled?: boolean;
}) {
  if (!actor.choices.length) return null;
  return <label className="arch-approve-as">
    <span>Approve as</span>
    <select aria-label="Approve as" value={actor.selected} disabled={disabled}
      onChange={event => actor.select(event.target.value)}>
      <option value="">Myself (Root)</option>
      {actor.choices.map(person => <option key={person.id} value={person.id}
        disabled={!actor.available.some(option => option.id === person.id)}>
        {person.display_name || person.username}
      </option>)}
    </select>
  </label>;
}
